import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PRIMARY_COLOR = '#3b82f6';
const ADDITIONAL_COLOR = '#8b5cf6';

export default function PDFZoneEditor({ fileUrl, zones = [], onZonesChange, zoneSigner = 'primary' }) {
    const [numPages, setNumPages] = useState(0);
    const [pageDimensions, setPageDimensions] = useState({});
    const [drawing, setDrawing] = useState(null);
    const [drawPreview, setDrawPreview] = useState(null);
    const [pdfData, setPdfData] = useState(null);
    const pageRefs = useRef([]);

    // Convert blob URL to ArrayBuffer for react-pdf compatibility
    useEffect(() => {
        if (!fileUrl) return;
        fetch(fileUrl)
            .then((res) => res.arrayBuffer())
            .then((buf) => setPdfData({ data: new Uint8Array(buf) }))
            .catch((err) => console.error('Failed to load PDF:', err));
    }, [fileUrl]);

    const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
        setNumPages(n);
    }, []);

    const measurePage = useCallback((pageNumber, pageRef) => {
        if (!pageRef) return;
        setTimeout(() => {
            const canvas = pageRef.querySelector('canvas');
            if (canvas) {
                setPageDimensions((prev) => ({
                    ...prev,
                    [pageNumber]: { width: canvas.clientWidth, height: canvas.clientHeight },
                }));
            }
        }, 200);
    }, []);

    const handleMouseDown = useCallback((pageNumber, e) => {
        const pageEl = pageRefs.current[pageNumber - 1];
        if (!pageEl) return;
        const rect = pageEl.getBoundingClientRect();
        setDrawing({
            startX: e.clientX - rect.left,
            startY: e.clientY - rect.top,
            page: pageNumber,
        });
        setDrawPreview(null);
    }, []);

    const handleMouseMove = useCallback((pageNumber, e) => {
        if (!drawing || drawing.page !== pageNumber) return;
        const pageEl = pageRefs.current[pageNumber - 1];
        if (!pageEl) return;
        const rect = pageEl.getBoundingClientRect();
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;
        setDrawPreview({
            x: Math.min(drawing.startX, currentX),
            y: Math.min(drawing.startY, currentY),
            width: Math.abs(currentX - drawing.startX),
            height: Math.abs(currentY - drawing.startY),
            page: pageNumber,
        });
    }, [drawing]);

    const handleMouseUp = useCallback((pageNumber, e) => {
        if (!drawing || drawing.page !== pageNumber) return;
        const pageEl = pageRefs.current[pageNumber - 1];
        if (!pageEl) return;
        const rect = pageEl.getBoundingClientRect();
        const endX = e.clientX - rect.left;
        const endY = e.clientY - rect.top;
        const pageDim = pageDimensions[pageNumber];

        if (pageDim) {
            const w = Math.abs(endX - drawing.startX);
            const h = Math.abs(endY - drawing.startY);
            if (w >= 20 && h >= 20) {
                const newZone = {
                    page: pageNumber,
                    x_percent: (Math.min(drawing.startX, endX) / pageDim.width) * 100,
                    y_percent: (Math.min(drawing.startY, endY) / pageDim.height) * 100,
                    width_percent: (w / pageDim.width) * 100,
                    height_percent: (h / pageDim.height) * 100,
                    signer: zoneSigner,
                };
                onZonesChange([...zones, newZone]);
            }
        }
        setDrawing(null);
        setDrawPreview(null);
    }, [drawing, pageDimensions, zoneSigner, zones, onZonesChange]);

    const handleDeleteZone = (idx) => {
        onZonesChange(zones.filter((_, i) => i !== idx));
    };

    if (!pdfData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, color: '#94a3b8' }}>
                <Typography>Loading PDF...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ maxHeight: '70vh', overflowY: 'auto', bgcolor: '#f1f5f9', borderRadius: '10px', p: 2 }}>
            <Document file={pdfData} onLoadSuccess={onDocumentLoadSuccess}>
                {Array.from({ length: numPages }, (_, i) => {
                    const pageNumber = i + 1;
                    const pageDim = pageDimensions[pageNumber];
                    const zonesForPage = zones
                        .map((z, idx) => ({ ...z, globalIndex: idx }))
                        .filter((z) => z.page === pageNumber);

                    return (
                        <Box
                            key={pageNumber}
                            ref={(el) => {
                                pageRefs.current[i] = el;
                                if (el) measurePage(pageNumber, el);
                            }}
                            sx={{
                                position: 'relative',
                                mb: 2,
                                cursor: 'crosshair',
                                userSelect: 'none',
                                boxShadow: 2,
                                display: 'inline-block',
                            }}
                            onMouseDown={(e) => handleMouseDown(pageNumber, e)}
                            onMouseMove={(e) => handleMouseMove(pageNumber, e)}
                            onMouseUp={(e) => handleMouseUp(pageNumber, e)}
                        >
                            <Page pageNumber={pageNumber} />

                            {/* Existing zones */}
                            {pageDim && zonesForPage.map((zone) => {
                                const color = zone.signer === 'primary' ? PRIMARY_COLOR : ADDITIONAL_COLOR;
                                return (
                                    <Box
                                        key={`zone-${zone.globalIndex}`}
                                        sx={{
                                            position: 'absolute',
                                            left: `${zone.x_percent}%`,
                                            top: `${zone.y_percent}%`,
                                            width: `${zone.width_percent}%`,
                                            height: `${zone.height_percent}%`,
                                            border: `2px dashed ${color}`,
                                            bgcolor: `${color}25`,
                                            pointerEvents: 'auto',
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                handleDeleteZone(zone.globalIndex);
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            sx={{
                                                position: 'absolute', top: -12, right: -12,
                                                bgcolor: '#ef4444', color: '#fff', p: 0.3,
                                                '&:hover': { bgcolor: '#dc2626' },
                                                boxShadow: 1,
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                            <Typography sx={{
                                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                                                fontSize: '0.7rem', fontWeight: 600, color,
                                                textTransform: 'uppercase', opacity: 0.8,
                                            }}>
                                                {zone.signer === 'primary' ? 'Primary' : 'Additional'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}

                            {/* Draw preview */}
                            {drawPreview && drawPreview.page === pageNumber && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: drawPreview.x,
                                        top: drawPreview.y,
                                        width: drawPreview.width,
                                        height: drawPreview.height,
                                        border: `2px dashed ${zoneSigner === 'primary' ? PRIMARY_COLOR : ADDITIONAL_COLOR}`,
                                        bgcolor: `${zoneSigner === 'primary' ? PRIMARY_COLOR : ADDITIONAL_COLOR}15`,
                                        pointerEvents: 'none',
                                    }}
                                />
                            )}

                            {/* Page label */}
                            <Typography sx={{
                                position: 'absolute', bottom: 4, right: 8,
                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                                fontSize: '0.7rem', color: '#94a3b8', bgcolor: 'rgba(255,255,255,0.8)',
                                px: 1, borderRadius: '4px',
                            }}>
                                Page {pageNumber}
                            </Typography>
                        </Box>
                    );
                })}
            </Document>
        </Box>
    );
}
